import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { EmergencyContactsManager } from "@/components/emergency-contacts/emergency-contacts-manager"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Typography } from "@/components/ui/typography"
import { useEmergencyContacts } from "@/hooks/use-emergency-contact-ids"
import { useUserType } from "@/hooks/use-user-type"
import { markEmergencyContactsSkippedThisSession } from "@/lib/emergency-contact-onboarding"

export const Route = createFileRoute("/_authed/onboarding/emergency-contacts")({
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = useNavigate()
  const { isIndividual } = useUserType()
  const [isSkipping, setIsSkipping] = useState(false)
  const [isFinishing, setIsFinishing] = useState(false)

  const { emergencyContacts } = useEmergencyContacts()

  const hasEmergencyContacts = emergencyContacts.length > 0

  if (!isIndividual) {
    navigate({ to: "/dashboard", search: { token: undefined }, replace: true })
    return null
  }

  const handleSkip = () => {
    setIsSkipping(true)
    markEmergencyContactsSkippedThisSession()
    navigate({ to: "/personal/goals", replace: true })
    setIsSkipping(false)
  }

  const finishOnboarding = () => {
    setIsFinishing(true)
    navigate({ to: "/personal/goals", replace: true })
    setIsFinishing(false)
  }

  return (
    <div className="mx-auto max-w-4xl space-y-8 p-6">
      <Card className="space-y-4 border-primary/20 bg-primary/5 p-6">
        <Typography as="h1" className="text-2xl font-semibold text-primary">
          Help us know who to trust
        </Typography>
        <Typography as="p" className="text-muted-foreground">
          If something happens to you, Legacy Keeper needs a clear record of who you trust to
          request access to your account. Adding emergency contacts now makes that process faster
          and less stressful for the people you care about.
        </Typography>
        <Typography as="p" className="text-sm text-muted-foreground">
          This step is optional. You can skip it now and manage emergency contacts any time from
          Account Settings.
        </Typography>
      </Card>

      <EmergencyContactsManager
        introTitle="Your emergency contacts"
        introDescription="Add one or more people with complete contact details. You can update this list whenever you need to."
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
        {hasEmergencyContacts ? (
          <Button type="button" onClick={finishOnboarding} disabled={isFinishing}>
            Done
          </Button>
        ) : (
          <Button type="button" variant="outline" onClick={handleSkip} disabled={isSkipping}>
            Skip for now
          </Button>
        )}
      </div>
    </div>
  )
}
